/** Picks and matches the plan-tour time slot that should pulse during onboarding. */
import { isSameDay, startOfDay } from 'date-fns';
import { TIME_SLOT_MINS } from '@/config/constants';

/** Minutes ahead of “now” for the suggested drop slot during plan-tour slot phase. */
export const ONBOARDING_TIME_SLOT_LOOKAHEAD_MINS = 30;

/**
 * @param {number} totalMinutes
 * @param {number} slotMins
 */
export function roundUpToSlotMinutes(totalMinutes, slotMins = TIME_SLOT_MINS) {
  return Math.ceil(totalMinutes / slotMins) * slotMins;
}

/**
 * @param {string} time "HH:mm"
 */
export function minutesFromTimeString(time) {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
}

/**
 * @param {number} totalMinutes
 * @returns {string}
 */
export function timeStringFromMinutes(totalMinutes) {
  const hour = Math.floor(totalMinutes / 60);
  const minute = totalMinutes % 60;
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
}

/**
 * Picks one time-slot to pulse during the plan-tour slot phase.
 *
 * @param {Object} params
 * @param {Date} [params.now]
 * @param {Date[]} params.visibleDayDates startOfDay for each visible column
 * @param {string[]} params.slotTimes "HH:mm" slots for the grid (15-min)
 * @param {number} params.startHour inclusive grid start (hour)
 * @param {number} params.endHour exclusive grid end (hour)
 * @returns {{ date: Date, time: string } | null}
 */
export function selectOnboardingTimeSlotHighlight({
  now = new Date(),
  visibleDayDates = [],
  slotTimes = [],
  startHour,
  endHour,
}) {
  if (!visibleDayDates.length || !slotTimes.length) {
    return null;
  }

  const startMinutes = startHour * 60;
  const endMinutes = endHour * 60;

  const targetInstant = new Date(now);
  targetInstant.setMinutes(
    targetInstant.getMinutes() + ONBOARDING_TIME_SLOT_LOOKAHEAD_MINS,
  );
  const targetDay = startOfDay(targetInstant);
  const targetMinutes = roundUpToSlotMinutes(
    targetInstant.getHours() * 60 + targetInstant.getMinutes(),
  );

  const targetVisibleDay = visibleDayDates.find((day) =>
    isSameDay(day, targetDay),
  );

  if (targetVisibleDay) {
    if (targetMinutes < startMinutes) {
      return { date: targetVisibleDay, time: slotTimes[0] };
    }
    if (targetMinutes < endMinutes) {
      const time = timeStringFromMinutes(targetMinutes);
      if (slotTimes.includes(time)) {
        return { date: targetVisibleDay, time };
      }
    }
  }

  return { date: visibleDayDates[0], time: slotTimes[0] };
}

/**
 * @param {{ date: Date, time: string } | null} highlight
 * @param {Date} columnDate
 * @param {string} slotTime
 */
export function isOnboardingTimeSlotHighlighted(highlight, columnDate, slotTime) {
  if (!highlight) {
    return false;
  }
  return isSameDay(highlight.date, columnDate) && highlight.time === slotTime;
}
